import { ModuleDto } from './dtos';

export interface ModuleRecoveryDraft {
    id: string;
    owner: string;
    recordId: string;
    copyId?: string;
    version: 1;
    savedAt: string;
    baseUpdatedAt: string;
    module: ModuleDto;
}
export const recoveryId = (owner: string, recordId: string, copyId?: string) => JSON.stringify([owner, 'managed-module', recordId, ...(copyId ? [copyId] : [])]);

function database(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('teacher-training-draft-recovery', 1);
        request.onupgradeneeded = () => request.result.createObjectStore('drafts', { keyPath: 'id' }).createIndex('owner', 'owner');
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('Recovery storage is blocked.'));
        request.onsuccess = () => resolve(request.result);
    });
}
async function transaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await database();
    try {
        return await new Promise<T>((resolve, reject) => {
            const tx = db.transaction('drafts', mode);
            const request = operation(tx.objectStore('drafts'));
            tx.oncomplete = () => resolve(request.result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } finally { db.close(); }
}
function validDraft(value: any, owner: string): value is ModuleRecoveryDraft {
    return value?.version === 1 && value.owner === owner && typeof value.recordId === 'string'
        && (value.copyId === undefined || typeof value.copyId === 'string')
        && value.id === recoveryId(owner, value.recordId, value.copyId) && Number.isFinite(Date.parse(value.savedAt))
        && typeof value.baseUpdatedAt === 'string' && typeof value.module?.name === 'string'
        && typeof value.module.module_key === 'string' && Array.isArray(value.module.fragments)
        && (value.recordId.startsWith('new:') ? value.module.module_key === '' : value.module.module_key === value.recordId);
}
export const draftRecoveryStore = {
    async list(owner: string): Promise<ModuleRecoveryDraft[]> {
        const rows = await transaction<any[]>('readonly', store => store.index('owner').getAll(owner));
        return rows.filter(row => validDraft(row, owner)).sort((a, b) => b.savedAt.localeCompare(a.savedAt));
    },
    async put(draft: ModuleRecoveryDraft): Promise<void> {
        await transaction('readwrite', store => store.put(draft));
    },
    async remove(id: string): Promise<void> {
        await transaction('readwrite', store => store.delete(id));
    },
};
